import cn from 'classnames';
import { Fragment } from 'react';
import { IconTooltip, Tooltip, TooltipInteractionEnum } from 'sonata-react-components';

import { useMediaQuery } from '@/hooks/useMediaQuery';
import { ToolButton } from '../ToolButton/ToolButton';
import { AttributionTabs } from './AttributionTabs/AttributionTabs';

import type { ToolGroupConfig, ToolSubGroupConfig } from '../ActionToolsPanel.types';

import './toolGroup.scss';

type Props = ToolGroupConfig & {
  'data-testid'?: string;
  isCollapsed?: boolean;
};

export function ToolGroup({
  activeTab,
  infoTooltip,
  isCollapsed = false,
  label,
  onChangeTab,
  subGroups,
  ...props
}: Props) {
  const headerContent = (
    <div className="toolGroup__header">
      <span className="toolGroup__label" title={label}>
        {label}
      </span>
      {!!infoTooltip && (
        <IconTooltip
          effect="solid"
          event="mouseenter"
          icon="fa-regular fa-circle-info"
          iconColor="var(--color-sonata-primary-500) !important"
          place="top"
        >
          <div className="actionToolsPanel__tooltipWrapper">{infoTooltip}</div>
        </IconTooltip>
      )}
    </div>
  );

  // Tabbed groups render a tab bar plus only the selected tab's tools; the collapsed
  // tooltip has no tab bar and shows the selected tab alone.
  const isCompact = useMediaQuery('(max-width: 1440px)');
  const tabs =
    isCompact && subGroups.every(({ tab }) => tab) ? subGroups.flatMap(({ tab }) => (tab ? [tab] : [])) : undefined;
  const visibleSubGroups = tabs ? subGroups.filter(({ tab }) => tab?.id === activeTab) : subGroups;

  const renderSubGroup = (subGroup: ToolSubGroupConfig, index: number) => {
    const box = (
      <div className={cn('toolGroup__box', { 'toolGroup__box--single': subGroup.tools.length === 1 })}>
        {subGroup.tools.map(tool => (
          <ToolButton key={tool.id} {...tool} />
        ))}
      </div>
    );

    return (
      <Fragment key={`${subGroup.description ?? 'subGroup'}-${index}`}>
        {index > 0 && <div className="toolGroup__divider" />}
        <div className="toolGroup__subGroup">
          {!tabs && !!subGroup.description && <span className="toolGroup__description">{subGroup.description}</span>}
          {subGroup.disabledTooltip ? (
            <Tooltip
              config={{
                arrow: true,
                component: <div className="actionToolsPanel__tooltipWrapper">{subGroup.disabledTooltip}</div>,
                forcePlacement: true,
                interactions: [TooltipInteractionEnum.hover],
                placement: 'top',
                portal: true,
                strategy: 'absolute'
              }}
            >
              {box}
            </Tooltip>
          ) : (
            box
          )}
        </div>
      </Fragment>
    );
  };

  return (
    <div
      className="toolGroup"
      data-testid={props['data-testid'] ?? `tool-group-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {isCollapsed && headerContent}
      <div className={cn('toolGroup__container', { 'toolGroup__container--isCollapsed': isCollapsed })}>
        {!isCollapsed && headerContent}
        {tabs && !isCollapsed && <AttributionTabs activeTab={activeTab} onChangeTab={onChangeTab} tabs={tabs} />}
        <div className="toolGroup__subGroups">{visibleSubGroups.map(renderSubGroup)}</div>
      </div>
    </div>
  );
}
